import React, { useEffect, useState } from 'react';

import moment from 'moment-timezone';

import { useSelector, useDispatch } from 'react-redux';
import { useProfileContext } from '../../../Contexts/ProfileContext';
import { setShowPaymentPlans, toggleDialog } from '../../../../redux/Slices/Global/globalSlice';

import { Button } from '../../../../components/ui/Button';
import { Spinner } from 'react-bootstrap';

import FeaturesList from '../Payment/FeaturesList';
import UpgradeDialog from '../Payment/UpgradeDialog';
import { useMutation } from 'react-query';
import { createPortalSession } from '../../../../queries';
import { useCustomToast } from '../../../Contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../../../utils/money';


function PlanDetailsCard() {
    const { openDialog, subLoaders, subscriptionInfo, companyDetails, packages } =
        useSelector((state) => state.global);

    const { setAction, setSelectedPricing, setSelectedPackage, setShowUpgrade } =
        useProfileContext();

    const { t, i18n } = useTranslation();
    const dispatch     = useDispatch();
    const { showToast } = useCustomToast();
    const [portalLoading, setPortalLoading] = useState(false);

    const portalMutation = useMutation(createPortalSession, {
        onSuccess: ({ portal_url }) => {
            window.location.href = portal_url;
        },
        onError: (error) => {
            showToast('error', t('billing.manage_billing'), error?.response?.data?.message ?? t('billing.portal_error'));
            setPortalLoading(false);
        },
    });

    const handleManageBilling = () => {
        setPortalLoading(true);
        portalMutation.mutate();
    };

    const renewalHandler = () => {
        setSelectedPackage(companyDetails?.package);
        setSelectedPricing(companyDetails?.subscription_pricing);
        dispatch(toggleDialog('paymentDialog'));
    };

    const upgrade = () => {
        setSelectedPackage(companyDetails?.package);
        setSelectedPricing(companyDetails?.subscription_pricing);
        dispatch(setShowPaymentPlans(true));
    };

    useEffect(() => {
        // show upgrade if there exists a package with higher basic_price
        let show = false;
        packages?.forEach((p) => {
            if (p.basic_price > (companyDetails?.basic_price ?? 0)) show = true;
        });
        setShowUpgrade(show);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subLoaders.companyDetails]);

    const start = companyDetails?.subscription?.start_date
        ? moment(companyDetails.subscription.start_date).format('DD-MM-YYYY')
        : '--';
    const end = companyDetails?.subscription?.end_date
        ? moment(companyDetails.subscription.end_date).format('DD-MM-YYYY')
        : '--';

    const isFinished = !!subscriptionInfo?.isFinished;

    return (
        <>
            {/* Outer card: auto height on small, fixed height on md+; scroll only the content area */}
            <div className="border rounded-[30px] w-full p-4 md:p-5 flex flex-col md:h-[355px]">

                {/* Header */}
                <div className="flex flex-wrap items-start md:items-center justify-between gap-3 min-h-[56px]">
                    <div className="min-w-0">
                        <div className="font-bold text-start text-lg md:text-[18px] leading-tight mb-1">
                            {t('billing.current_plan')}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <div
                                className={[
                                    'border rounded-full px-3 py-1 font-bold text-sm',
                                    (isFinished || new Date(companyDetails?.subscription?.end_date) < new Date())
                                        ? 'bg-red-50 text-red-800'
                                        : 'bg-green-50 text-green-800',
                                ].join(' ')}
                            >
                                {companyDetails?.package?.title ?? '--'} {t('billing.plan_label')}
                            </div>

                            {/* Price of this grant: only when the admin stored a currency with it. */}
                            {formatMoney(companyDetails?.subscription?.amount, companyDetails?.subscription?.currency, i18n.language) && (
                                <div className="text-sm md:text-base whitespace-nowrap" data-testid="plan-price">
                                    {formatMoney(companyDetails.subscription.amount, companyDetails.subscription.currency, i18n.language)}
                                </div>
                            )}
                            <div className="text-sm md:text-base whitespace-nowrap" data-testid="plan-status">
                                {isFinished ? t('billing.status_ended') : t('billing.status_active')}
                            </div>
                        </div>
                    </div>

                    {/* (No change-plan / renew / manage-billing buttons: customers do not buy inside the platform.) */}
                </div>

                <hr className="w-full my-3" />

                {/* Scrollable content region on md+ to keep the card height exact */}
                <div className="flex-1 min-h-0 flex flex-col gap-2">
                    <div className="font-bold mb-0.5">{t('billing.current_billing_cycle')}</div>
                    <div className="text-sm mb-2">{start} - {end}</div>

                    <div className="font-bold">{t('billing.plan_details')}</div>

                    {/* Features list: allow scroll only when needed on md+ */}
                    <div className="flex-1 min-h-0   md:pr-1">
                        <FeaturesList actionBtn={false} selectedPackage={companyDetails?.package} />
                    </div>
                </div>
            </div>

            {openDialog?.upgrade && <UpgradeDialog />}
        </>
    );
}

export default PlanDetailsCard;
